import React from 'react';
import styled from 'styled-components';

interface SiteSettings {
  site_title: string;
  site_description: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  [key: string]: any;
}

interface SEOSettingsProps {
  settings: SiteSettings;
  onUpdate: (updates: Partial<SiteSettings>) => void;
}

const Section = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.3rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const HelpText = styled.small`
  color: #666;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

const CharCount = styled.span<{ $warning?: boolean }>`
  float: right;
  font-size: 0.75rem;
  color: ${({ $warning }) => $warning ? '#dc3545' : '#6c757d'};
  margin-top: 0.25rem;
`;

const PreviewBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;
`;

const PreviewTitle = styled.div`
  color: #1a0dab;
  font-size: 1.125rem;
  text-decoration: underline;
  margin-bottom: 0.25rem;
`;

const PreviewURL = styled.div`
  color: #006621;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const PreviewDescription = styled.div`
  color: #545454;
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ErrorText = styled.small`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
`;

export const TRACKING_FIELDS = [
  {
    id: 'google_tag_id',
    label: 'ID da tag do Google',
    placeholder: 'AW-123456789 ou G-XXXXXXX',
    pattern: /^(G|AW|GT)-[A-Z0-9]{4,20}$/i,
    help: 'Google Ads > Metas > Tag do Google. Carrega o gtag em todas as páginas públicas.',
  },
  {
    id: 'google_ads_whatsapp_conversion',
    label: 'Conversão do Google Ads no clique do WhatsApp',
    placeholder: 'AW-123456789/AbCdEfGh',
    pattern: /^AW-[0-9]{6,15}\/[A-Za-z0-9_-]{4,64}$/,
    help: 'Valor do "send_to" da ação de conversão. Disparado em todo clique no WhatsApp.',
  },
  {
    id: 'facebook_pixel_id',
    label: 'ID do pixel da Meta',
    placeholder: '1234567890123456',
    pattern: /^[0-9]{8,20}$/,
    help: 'Gerenciador de Eventos da Meta. Registra PageView, Contact (WhatsApp) e Lead (formulários).',
  },
  {
    id: 'google_site_verification',
    label: 'Verificação do Search Console',
    placeholder: 'Cole o código ou a meta tag inteira',
    pattern: /^([A-Za-z0-9_-]{20,100}|<meta[^>]*content=["'][A-Za-z0-9_-]{20,100}["'][^>]*>)$/,
    help: 'Search Console > Adicionar propriedade > Tag HTML.',
  },
] as const;

export const invalidTrackingFields = (settings: Record<string, any>): string[] =>
  TRACKING_FIELDS.filter((field) => {
    const value = String(settings[field.id] || '').trim();
    return value !== '' && !field.pattern.test(value);
  }).map((field) => field.label);

const SEOSettings: React.FC<SEOSettingsProps> = ({ settings, onUpdate }) => {
  const handleChange = (field: string, value: string) => {
    onUpdate({ [field]: value });
  };

  const titleLength = settings.site_title?.length || 0;
  const descriptionLength = settings.site_description?.length || 0;
  const metaTitleLength = settings.meta_title?.length || 0;
  const metaDescriptionLength = settings.meta_description?.length || 0;

  return (
    <>
      <Section>
        <SectionTitle>🔍 Configurações de SEO</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Configure os metadados para melhorar o posicionamento do site nos buscadores.
        </p>

        <FormGroup>
          <Label htmlFor="site_title">Título do Site</Label>
          <Input
            id="site_title"
            type="text"
            value={settings.site_title}
            onChange={(e) => handleChange('site_title', e.target.value)}
            placeholder="ImobiPro - Portal Imobiliário"
          />
          <HelpText>
            Título principal que aparece na aba do navegador e nos resultados de busca
            <CharCount $warning={titleLength > 60}>{titleLength}/60</CharCount>
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="site_description">Descrição do Site</Label>
          <TextArea
            id="site_description"
            value={settings.site_description}
            onChange={(e) => handleChange('site_description', e.target.value)}
            placeholder="Encontre o imóvel perfeito com a ImobiPro. Casas, apartamentos e terrenos com a melhor qualidade e preços do mercado."
          />
          <HelpText>
            Descrição que aparece nos resultados de busca do Google
            <CharCount $warning={descriptionLength > 160}>{descriptionLength}/160</CharCount>
          </HelpText>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>🏷️ Meta Tags Customizadas</SectionTitle>
        
        <FormGroup>
          <Label htmlFor="meta_title">Meta Title</Label>
          <Input
            id="meta_title"
            type="text"
            value={settings.meta_title || ''}
            onChange={(e) => handleChange('meta_title', e.target.value)}
            placeholder="Deixe vazio para usar o título do site"
          />
          <HelpText>
            Título específico para SEO (se diferente do título do site)
            <CharCount $warning={metaTitleLength > 60}>{metaTitleLength}/60</CharCount>
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="meta_description">Meta Description</Label>
          <TextArea
            id="meta_description"
            value={settings.meta_description || ''}
            onChange={(e) => handleChange('meta_description', e.target.value)}
            placeholder="Deixe vazio para usar a descrição do site"
          />
          <HelpText>
            Descrição específica para SEO (se diferente da descrição do site)
            <CharCount $warning={metaDescriptionLength > 160}>{metaDescriptionLength}/160</CharCount>
          </HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="meta_keywords">Palavras-chave</Label>
          <Input
            id="meta_keywords"
            type="text"
            value={settings.meta_keywords || ''}
            onChange={(e) => handleChange('meta_keywords', e.target.value)}
            placeholder="imóveis, casas, apartamentos, venda, locação, São Paulo"
          />
          <HelpText>Palavras-chave separadas por vírgula (opcional - pouco usado pelos buscadores modernos)</HelpText>
        </FormGroup>
      </Section>

      <Section>
        <SectionTitle>📈 Rastreamento e anúncios</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          IDs usados pelo Google Ads, pelo remarketing da Meta e pelo Search Console. As tags não
          rodam no painel administrativo. Deixe vazio para desativar.
        </p>

        {TRACKING_FIELDS.map((field) => {
          const value: string = settings[field.id] || '';
          const invalid = value.trim() !== '' && !field.pattern.test(value.trim());
          return (
            <FormGroup key={field.id}>
              <Label htmlFor={field.id}>{field.label}</Label>
              <Input
                id={field.id}
                type="text"
                value={value}
                onChange={(e) => handleChange(field.id, e.target.value)}
                placeholder={field.placeholder}
                aria-invalid={invalid}
              />
              {invalid ? (
                <ErrorText>Formato inválido. Exemplo: {field.placeholder}</ErrorText>
              ) : (
                <HelpText>{field.help}</HelpText>
              )}
            </FormGroup>
          );
        })}
      </Section>

      <Section>
        <SectionTitle>👀 Preview do Google</SectionTitle>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Veja como seu site aparecerá nos resultados de busca do Google:
        </p>
        
        <PreviewBox>
          <PreviewTitle>
            {settings.meta_title || settings.site_title || 'Título do Site'}
          </PreviewTitle>
          <PreviewURL>https://seusite.com.br</PreviewURL>
          <PreviewDescription>
            {(settings.meta_description || settings.site_description || 'Descrição do site...').substring(0, 160)}
            {(settings.meta_description || settings.site_description || '').length > 160 && '...'}
          </PreviewDescription>
        </PreviewBox>
      </Section>
    </>
  );
};

export default SEOSettings;